import React from "react";
import clsx from "clsx";

import "./Button.css";
import "./ButtonPrimary.css";
import "./ButtonSecondary.css";
import "./ButtonDestructive.css";
import "./ButtonTextLike.css";

const ButtonType = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  DESTRUCTIVE: "destructive",
  DISABLED: "disabled",
  TEXTLIKE: "textLike",
} as const;

interface ButtonProps {
  onClick: () => void;
  isDisabled?: boolean;
  children: React.ReactNode;
}

interface ButtonWithTypeProps extends ButtonProps {
  type?: (typeof ButtonType)[keyof typeof ButtonType];
}

const ButtonComponent: React.FC<ButtonWithTypeProps> = ({
  type,
  onClick,
  isDisabled = false,
  children,
}) => {
  const buttonType = isDisabled ? ButtonType.DISABLED : type;
  const cls = clsx(
    "sid-button",
    buttonType === ButtonType.PRIMARY && ["sid-button-primary", "sid-button-font"],
    buttonType === ButtonType.SECONDARY && ["sid-button-secondary", "sid-button-font"],
    buttonType === ButtonType.DESTRUCTIVE && ["sid-button-destructive", "sid-button-font"],
    buttonType === ButtonType.DISABLED && ["sid-button-disabled", "sid-button-font"],
    buttonType === ButtonType.TEXTLIKE && ["sid-button-textLike", "sid-link"],
  );

  /*
    Do not use type "submit" or "reset" as this library does not use <form> elements
    Does not use the "disable" prop, so that the button is still [Tab] navigable / focusable
  */
  return (
    <button
      type="button"
      className={cls}
      aria-disabled={isDisabled}
      onClick={!isDisabled ? onClick : null}
    >
      {children}
    </button>
  );
};

const buttonWithType =
  (type: (typeof ButtonType)[keyof typeof ButtonType]): React.FC<ButtonProps> =>
  ({ isDisabled = false, onClick, children }) =>
    (
      <ButtonComponent type={type} onClick={onClick} isDisabled={isDisabled}>
        {children}
      </ButtonComponent>
    );

export const Button = {
  Primary: buttonWithType(ButtonType.PRIMARY),
  Secondary: buttonWithType(ButtonType.SECONDARY),
  Destructive: buttonWithType(ButtonType.DESTRUCTIVE),
  LinkLike: buttonWithType(ButtonType.TEXTLIKE),
};
